import { createIcon } from "@chakra-ui/react";
import isObject from "lodash/isObject";
import theme from "@shared/theme/utils/theme";
import { IconProps } from "./Icon.props";

// Icon color scheme per Figma wireframes:
// - Primary fill (.icon-primary): 900 shade - dark, high contrast
// - Secondary accent (.icon-secondary): 500 shade at 40% opacity - lighter accent for dots/badges

function getColorScheme(colorPalette: string, shade: number): string {
  if (colorPalette === "currentColor") return "currentColor";
  if (isObject(theme(`colors.${colorPalette}`)))
    return `${colorPalette}.${shade}`;
  return colorPalette;
}

export default function createWebIcon({
  name,
  colorPalette = "primary",
  path,
  css: externalCss,
}: Omit<IconProps, "path"> & { path: JSX.Element }): ReturnType<
  typeof createIcon
> {
  // Support "currentColor" to inherit color from parent element
  const useCurrentColor = colorPalette === "currentColor";

  // Primary: 900 shade (dark)
  const primaryColor = getColorScheme(colorPalette, 900);
  // Secondary: 500 shade (used at 40% opacity for accents)
  const secondaryColor = getColorScheme(colorPalette, 500);

  // Convert token to CSS variable name (e.g., "primary.500" -> "--chakra-colors-primary-500")
  const secondaryCssVar = useCurrentColor
    ? "currentColor"
    : `var(--chakra-colors-${secondaryColor.replace(".", "-")})`;

  // Base CSS for two-tone icon styling
  const iconCss = {
    // Primary fill uses currentColor (respects color prop override)
    "& .icon-primary": { fill: "currentColor" },
    // Secondary uses 500 shade at 40% opacity for accents (dots, badges)
    "& .icon-secondary": {
      fill: secondaryCssVar,
      opacity: 0.4,
    },
    "& .icon-outline": { stroke: "currentColor" },
    "& path[stroke='currentColor']": { stroke: "currentColor" },
  };

  // Merge external CSS with icon CSS (icon CSS takes precedence for icon-specific styles)
  const mergedCss = externalCss ? [iconCss, externalCss] : iconCss;

  return createIcon({
    displayName: name,
    viewBox: "0 0 24 24",
    defaultProps: {
      // NOTE: Do NOT set h/w/boxSize here - sizing is handled by validateKeys
      "aria-hidden": true, // Icons are decorative by default; use aria-label to override
      // Set default color to 900 shade - can be overridden by passing color prop
      color: useCurrentColor ? "inherit" : primaryColor,
      css: mergedCss,
    },
    path: <g fill="none">{path}</g>,
  });
}

export const baseCreateIcon = createIcon;
